import React, { useEffect, useRef, useState } from 'react';
import { MiscEmployeeTask, Project } from '../../interfaces/EmployeeInterface';
import { AccountCode } from '../../interfaces/AccountCodeInterface';
import {
  PayrollEntryResult, PayrollMiscTaskBody,
} from '../../interfaces/TimesheetEditInterface';
import { parseAmt, ymdLabel } from './clockParse';
import { getAccountCodesCached } from './lookups';
import ProjectPicker, { PickedProject } from './ProjectPicker';
import { EntryErrorBanner, EntryModalShell, performEntrySave } from './EntryModalShell';
import { AmountEchoParts } from './echoParts';

/**
 * Misc task editor (M2-B). Description + account code + amount per the design;
 * the implemented contract (§8.4) additionally REQUIRES a project on create
 * (`projectID` positive int, create-only), so create carries the project
 * picker — on edit the project is locked and description/account/amount edit.
 */

export interface MiscTaskTarget {
  row: MiscEmployeeTask;
  project: Project | null;
}

export interface MiscCarry {
  project: PickedProject | null;
  accountCodeID: number | null;
}

export interface MiscTaskModalProps {
  employeeID: number;
  payPeriodID: number;
  ymd: string;
  employeeName: string;
  editSessionID: string;
  entry: MiscTaskTarget | null;
  carry: MiscCarry | null;
  onSaved: (result: PayrollEntryResult, close: boolean, carry: MiscCarry) => void;
  onCancel: () => void;
  onConflict: (message: string) => void;
}

const MiscTaskModal: React.FC<MiscTaskModalProps> = ({
  employeeID, payPeriodID, ymd, employeeName, editSessionID,
  entry, carry, onSaved, onCancel, onConflict,
}) => {
  const isEdit = entry != null;
  const [project, setProject] = useState<PickedProject | null>(carry?.project ?? null);
  const [desc, setDesc] = useState<string>(isEdit ? entry!.row.miscDescription ?? '' : '');
  const [accountCodeID, setAccountCodeID] = useState<number | null>(
    isEdit ? entry!.row.accountCode?.accountCodeID ?? null : carry?.accountCodeID ?? null
  );
  const [amountRaw, setAmountRaw] = useState<string>(isEdit ? String(entry!.row.amountClaimed ?? '') : '');
  const [accounts, setAccounts] = useState<AccountCode[]>([]);
  const [bad, setBad] = useState<'desc' | 'amount' | null>(null);
  const [banner, setBanner] = useState<{ title: string; body?: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const descRef = useRef<HTMLInputElement>(null);
  const amountRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getAccountCodesCached().then(setAccounts).catch(() => setAccounts([]));
  }, []);

  const handleSave = async (close: boolean) => {
    if (desc.trim() === '') { setBad('desc'); descRef.current?.focus(); return; }
    const amount = parseAmt(amountRaw);
    if (amount == null || amount < 0) { setBad('amount'); amountRef.current?.focus(); return; }
    if (!isEdit && project == null) {
      setBanner({ title: 'Pick a project first.', body: 'Misc tasks are claimed against a project.' });
      return;
    }

    const body: PayrollMiscTaskBody = isEdit
      ? {
        amountClaimed: amount,
        miscDescription: desc.trim(),
        ...(accountCodeID != null ? { accountCodeID } : {}),
        editSessionID,
      }
      : {
        employeeID,
        payPeriodID,
        projectID: project!.projectID,
        amountClaimed: amount,
        miscDescription: desc.trim(),
        ...(accountCodeID != null ? { accountCodeID } : {}),
        entryDate: ymd,
        editSessionID,
      };

    setSaving(true);
    setBanner(null);
    const outcome = await performEntrySave('miscTask', isEdit ? entry!.row.miscEmployeeTaskID : null, body);
    setSaving(false);
    if (!outcome.ok) {
      if (outcome.conflict) onConflict(outcome.message);
      else setBanner({ title: 'Could not save the misc task.', body: outcome.message });
      return;
    }
    onSaved(outcome.result, close, { project, accountCodeID });
  };

  return (
    <EntryModalShell
      kind="miscTask"
      mode={isEdit ? 'edit' : 'add'}
      subtitle={`${ymdLabel(ymd)} · ${employeeName}`}
      banner={banner ? <EntryErrorBanner title={banner.title} body={banner.body} /> : null}
      echo={<AmountEchoParts raw={amountRaw} />}
      saving={saving}
      onSave={handleSave}
      onCancel={onCancel}
    >
      <div className="field">
        <label>Project</label>
        {isEdit ? (
          <div className="pe-fixed">
            <span className="mono">{entry!.project?.altProjectID ?? ''}</span>
            {' '}· {entry!.project?.name ?? `Project #${entry!.row.projectID}`}
          </div>
        ) : (
          <ProjectPicker value={project} onChange={setProject} autoFocus placeholder="Search projects…" />
        )}
      </div>
      <div className="field">
        <label>Description</label>
        <input
          ref={descRef}
          className={`pe-in sans${bad === 'desc' ? ' bad' : ''}`}
          value={desc}
          onChange={(ev) => { setDesc(ev.target.value); if (bad === 'desc') setBad(null); }}
          placeholder="Scrap out unit 14"
          autoFocus={isEdit}
          autoComplete="off"
        />
      </div>
      <div className="row2">
        <div className="field">
          <label>Account code</label>
          <select
            className="pe-in sans"
            value={accountCodeID ?? ''}
            onChange={(ev) => setAccountCodeID(ev.target.value === '' ? null : Number(ev.target.value))}
          >
            <option value="">Select account…</option>
            {accounts.map((c) => (
              <option key={c.accountCodeID} value={c.accountCodeID}>{c.altAccountCodeID} · {c.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Amount claimed</label>
          <input
            ref={amountRef}
            className={`pe-in${bad === 'amount' ? ' bad' : ''}`}
            value={amountRaw}
            onChange={(ev) => { setAmountRaw(ev.target.value); if (bad === 'amount') setBad(null); }}
            placeholder="0.00"
            autoComplete="off"
            spellCheck={false}
          />
        </div>
      </div>
    </EntryModalShell>
  );
};

export default MiscTaskModal;
